import { useState, useEffect } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import {
  ArrowLeft,
  Lock,
  Loader2,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Check,
  X
} from 'lucide-react'
import { useAuth } from '../lib/auth'
import NexaticeIcon from '../assets/nexatice-icon.svg'

interface PasswordRequirement {
  label: string
  test: (password: string) => boolean
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /[0-9]/.test(p) },
  { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) }
]

export function ResetPassword() {
  const { resetPassword } = useAuth()
  const [token, setToken] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showRequirements, setShowRequirements] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tokenParam = params.get('token')
    setToken(tokenParam)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token) {
      setError('Invalid or missing reset token')
      return
    }

    if (!password || !confirmPassword) {
      setError('Please fill in all fields')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    const allRequirementsMet = PASSWORD_REQUIREMENTS.every((req) => req.test(password))
    if (!allRequirementsMet) {
      setError('Password does not meet all requirements')
      return
    }

    setIsLoading(true)
    const result = await resetPassword(token, password)
    setIsLoading(false)

    if (result.success) {
      setSuccess(true)
    } else {
      setError(result.message)
    }
  }

  if (!token) {
    return (
      <div className="relative min-h-screen">
        <div className="noise-overlay" />
        <MeshGradient />
        <Navbar currentPage="auth" />

        <main className="relative z-10 pt-32 pb-20 px-6">
          <div className="max-w-md mx-auto">
            <div className="auth-card">
              <div className="auth-error-full">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2>Invalid Reset Link</h2>
                <p>
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
                <a href="/forgot-password" className="auth-submit mt-6">
                  Request New Link
                </a>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="auth" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-md mx-auto">
          <a
            href="/login"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </a>

          <div className="auth-card">
            <div className="auth-header">
              <img src={NexaticeIcon} alt="Nexatice" className="auth-logo" />
              <h1>Set new password</h1>
              <p>Enter your new password below</p>
            </div>

            {success ? (
              <div className="auth-success">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                <h2>Password reset successful</h2>
                <p>Your password has been reset. You can now sign in with your new password.</p>
                <a href="/login" className="auth-submit mt-6">
                  Sign In
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="auth-form">
                {error && (
                  <div className="auth-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="password">New Password</label>
                  <div className="input-wrapper">
                    <Lock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setShowRequirements(true)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="input-action"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {showRequirements && password && (
                    <div className="password-requirements">
                      {PASSWORD_REQUIREMENTS.map((req, i) => (
                        <div
                          key={i}
                          className={`requirement ${req.test(password) ? 'met' : 'unmet'}`}
                        >
                          {req.test(password) ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <X className="w-3 h-3" />
                          )}
                          {req.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="input-wrapper">
                    <Lock className="input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="input-action"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="input-hint error">Passwords do not match</p>
                  )}
                </div>

                <button type="submit" className="auth-submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

